import React, { ForwardRefRenderFunction, useImperativeHandle, useState } from "react";

import { CircularProgress, Divider, Tooltip, type MenuProps } from "@mui/material";

import { Menu, MenuItem, MenuItemText, MenuSubheader, MenuSizeProps } from "./styled";

type ActionFunction = () => void;
interface CommomActionProps {
  hidden?: boolean;
}

interface ActionButton extends CommomActionProps {
  onClick?: ActionFunction;
  label: string | React.ReactNode;
  isDisabled?: boolean;
  tooltipMessage?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  isError?: boolean;
  isLoading?: boolean;
  autoClose?: boolean;
}
interface ActionDivider extends CommomActionProps {
  isDivider: true;
}

interface ActionSubheader extends CommomActionProps {
  isSubHeader: true;
  label: string | React.ReactNode;
  isError?: boolean;
}

type ActionElement = ActionButton | ActionDivider | ActionSubheader;
type ActionElements = Array<ActionElement>;

type Actions = ActionElements;

export interface AppMenuProps extends Omit<MenuProps, "open"> {
  open?: boolean;
  actions?: Actions;
  children?: React.ReactNode;
  size?: MenuSizeProps;
}

function isActionDivider(action: ActionElement): action is ActionDivider {
  return (action as ActionDivider).isDivider === true;
}

function isSubHeader(action: ActionElement): action is ActionSubheader {
  return (action as ActionSubheader).isSubHeader === true;
}

export interface ResourceMenuHandles {
  open: (element: HTMLElement) => void;
  close: () => void;
}

const AppMenuComp: ForwardRefRenderFunction<ResourceMenuHandles, AppMenuProps> = (props, ref) => {
  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState<null | HTMLElement>(null);

  const { actions, children, size, ...rest } = props;

  const open = (element: HTMLElement) => {
    setOptionsMenuAnchor(element);
  };

  const close = () => {
    setOptionsMenuAnchor(null);
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const onClick = (action: ActionButton) => {
    const { autoClose = true } = action;

    action.onClick?.();
    // if (autoClose === true) close();
  };

  const renderActionOptions = () => {
    if (!actions || !actions.length) {
      return null;
    }

    return actions.map((action, index) => {
      if (isActionDivider(action)) {
        return <Divider style={{ margin: "4px 0px" }} key={`action-${index}-isDivider`} />;
      }

      if (isSubHeader(action)) {
        return (
          <MenuSubheader key={`action-${index}-isSubHeader-${action.label}`} size={size}>
            {action.label}
          </MenuSubheader>
        );
      }

      return (
        <Tooltip
          key={`action-${index}-isMenuItem-${action.label}`}
          title={action.tooltipMessage || ""}
        >
          <div>
            <MenuItem
              disabled={action.isDisabled}
              onClick={() => onClick(action)}
              isError={action.isError}
              size={size}
            >
              {action.isLoading ? <CircularProgress size={24} /> : action.leftContent}
              <MenuItemText>{action.label}</MenuItemText>
              {action.rightContent}
            </MenuItem>
          </div>
        </Tooltip>
      );
    });
  };

  return (
    <Menu
      anchorEl={optionsMenuAnchor}
      onClose={close}
      open={Boolean(optionsMenuAnchor)}
      variant="menu"
      autoFocus={false}
      disableAutoFocusItem={true}
      MenuListProps={{
        "aria-labelledby": "basic-button",
        "aria-hidden": undefined,
        autoFocusItem: false,
        style: {
          padding: "0px !important",
        },
      }}
      {...rest}
    >
      {renderActionOptions()}
      {children}
    </Menu>
  );
};

export const AppMenu = React.forwardRef(AppMenuComp);
